/**
 * NavSection — Grouped section with collapsible header.
 * Story 13.2
 */

import { useState } from "react";
import { ChevronDown } from "lucide-react";
import type { NavItemConfig } from "../side-nav-config";
import { NavItem } from "./NavItem";

interface NavSectionProps {
  label: string;
  items: NavItemConfig[];
  currentPath: string;
  isCollapsed: boolean;
  isActiveItem: (itemPath: string, currentPath: string) => boolean;
  onNavigate: (path: string) => void;
  /** Optional badge counts keyed by item id */
  badgeCounts?: Record<string, number>;
}

export function NavSection({
  label,
  items,
  currentPath,
  isCollapsed,
  isActiveItem: checkActive,
  onNavigate,
  badgeCounts,
}: NavSectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  if (items.length === 0) return null;

  return (
    <div className="mb-2">
      {/* Section header — only show when nav is expanded */}
      {!isCollapsed && (
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] hover:text-[var(--text-secondary)] transition-interactive"
          aria-expanded={isExpanded}
        >
          <span>{label}</span>
          <ChevronDown
            size={14}
            className={`transition-transform duration-150 ${
              isExpanded ? "rotate-0" : "-rotate-90"
            }`}
            aria-hidden="true"
          />
        </button>
      )}

      {/* Section items */}
      {(isCollapsed || isExpanded) && (
        <ul className="space-y-0.5 px-1" role="list">
          {items.map((item) => (
            <NavItem
              key={item.id}
              id={item.id}
              label={item.label}
              path={item.path}
              icon={item.icon}
              isActive={checkActive(item.path, currentPath)}
              isCollapsed={isCollapsed}
              badgeCount={badgeCounts?.[item.id]}
              onClick={onNavigate}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
